"use client";

import React, { ComponentType, useCallback, useEffect, useState } from "react";
import { ReactionButton, ReactionButtonProps } from "./ReactionButton";

export interface ReactionDataConfig {
  // Data fetching
  fetchInitialData: () => Promise<{ total: number; user: number }>;
  submitReaction: (count: number) => Promise<void>;

  // Optional features
  pollingInterval?: number;
  onReactionSuccess?: (count: number) => void;
  onReactionError?: (error: Error) => void;
  analytics?: {
    trackClick: (data: unknown) => void;
    trackSubmit: (data: unknown) => void;
  };
}

interface WithReactionDataProps {
  config: ReactionDataConfig;
  // Props to pass through to ReactionButton
  size?: ReactionButtonProps["size"];
  className?: string;
  icon?: ReactionButtonProps["icon"];
  enableParticles?: boolean;
  animationStyle?: ReactionButtonProps["animationStyle"];
  onAuthRequired?: () => void;
  authenticated?: boolean;
}

export function withReactionData<P extends object>(
  WrappedComponent: ComponentType<ReactionButtonProps & P>
) {
  return function ReactionButtonWithData({
    config,
    authenticated = true,
    onAuthRequired,
    ...props
  }: WithReactionDataProps & Omit<P, keyof ReactionButtonProps>) {
    const [totalCount, setTotalCount] = useState(0);
    const [userCount, setUserCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
      try {
        const { total, user } = await config.fetchInitialData();
        setTotalCount(total);
        setUserCount(user);
        return { total, user };
      } catch (error) {
        config.onReactionError?.(error as Error);
        return { total: 0, user: 0 };
      }
    }, [config]);

    // Initial load
    useEffect(() => {
      setLoading(true);
      fetchData().finally(() => setLoading(false));
    }, [fetchData]);

    // Optional polling
    useEffect(() => {
      if (!config.pollingInterval) return;

      const interval = setInterval(() => {
        fetchData();
      }, config.pollingInterval);

      return () => clearInterval(interval);
    }, [config.pollingInterval, fetchData]);

    const handleReaction = useCallback(
      async (count: number) => {
        // Optimistic update
        setUserCount((prev) => prev + count);
        setTotalCount((prev) => prev + count);

        try {
          await config.submitReaction(count);
          config.onReactionSuccess?.(count);
          config.analytics?.trackSubmit({ count });
        } catch (error) {
          // Revert on error
          setUserCount((prev) => prev - count);
          setTotalCount((prev) => prev - count);
          config.onReactionError?.(error as Error);
        }
      },
      [config]
    );

    const handleClick = useCallback(() => {
      if (!authenticated && onAuthRequired) {
        onAuthRequired();
        return;
      }

      config.analytics?.trackClick({
        totalCount: totalCount + userCount,
        authenticated,
      });
    }, [
      authenticated,
      onAuthRequired,
      config.analytics,
      totalCount,
      userCount,
    ]);

    return (
      <WrappedComponent
        totalCount={totalCount}
        userCount={userCount}
        loading={loading}
        onReaction={handleReaction}
        onClick={!authenticated ? handleClick : undefined}
        {...(props as P)}
      />
    );
  };
}

// Export the composed component type
export const ReactionButtonWithData = withReactionData(ReactionButton);
